import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { heroSlides } from "../mock";

const HeroCarousel = () => {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStartX = React.useRef(null);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => {
      setIdx((i) => (i + 1) % heroSlides.length);
    }, 6000);
    return () => clearInterval(t);
  }, [paused]);

  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 40) {
      if (dx < 0) setIdx((i) => (i + 1) % heroSlides.length);
      else setIdx((i) => (i - 1 + heroSlides.length) % heroSlides.length);
    }
    touchStartX.current = null;
  };

  const slide = heroSlides[idx];

  return (
    <section
      className="relative pt-20"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="max-w-[1400px] mx-auto px-4 lg:px-8 pt-6">
        <div
          className="relative rounded-3xl overflow-hidden h-[560px] lg:h-[620px]"
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          {/* Image with crossfade */}
          {heroSlides.map((s, i) => (
            <div
              key={s.id}
              className={`absolute inset-0 transition-opacity duration-1000 ${
                i === idx ? "opacity-100" : "opacity-0"
              }`}
            >
              <img
                src={s.image}
                alt={s.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/55 to-black/30"></div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>
            </div>
          ))}

          {/* Content */}
          <div className="relative z-10 h-full flex items-center">
            <div className="px-8 lg:px-16 max-w-[640px]">
              {slide.badge && (
                <div
                  key={`b-${idx}`}
                  className="animate-fadeIn inline-block mb-5 px-3 py-1 text-xs tracking-[0.18em] font-semibold text-white/90 border border-white/30 rounded-full backdrop-blur-sm"
                >
                  {slide.badge}
                </div>
              )}
              <h1
                key={`t-${idx}`}
                className="animate-fadeIn font-display text-[34px] leading-[1.1] lg:text-[52px] lg:leading-[1.05] font-bold text-white"
              >
                {slide.title}
              </h1>
              <div key={`c-${idx}`} className="animate-fadeIn mt-8">
                <Link
                  to="/signup"
                  data-testid={`hero-cta-${idx}`}
                  className="inline-flex items-center gap-2 px-6 py-3 bb-blue text-white font-medium rounded-lg transition-all hover:gap-3"
                >
                  {slide.cta}
                  <ArrowRight size={18} />
                </Link>
              </div>
            </div>
          </div>

          {/* Arrows — desktop only (avoid overlapping headline on mobile) */}
          <button
            onClick={() => setIdx((idx - 1 + heroSlides.length) % heroSlides.length)}
            className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-md border border-white/15 items-center justify-center text-white transition-all"
            aria-label="Previous"
            data-testid="hero-prev"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            onClick={() => setIdx((idx + 1) % heroSlides.length)}
            className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-md border border-white/15 items-center justify-center text-white transition-all"
            aria-label="Next"
            data-testid="hero-next"
          >
            <ChevronRight size={20} />
          </button>

          {/* Dots */}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
            {heroSlides.map((_, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                className={`h-1.5 rounded-full transition-all ${
                  i === idx ? "w-8 bg-white" : "w-1.5 bg-white/40 hover:bg-white/60"
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroCarousel;
